import { useEffect } from "react";

import { ABSOLUTE_MAX_RECURRING_SERIES_LIMIT, DEFAULT_RECURRING_SERIES_LIMIT } from "@performyard/models";
import type { MeetingModelJson, VideoConferencingProviderEnum } from "@performyard/service-contracts";
import type { GetUserCalendarIntegrationStatusResponse } from "@performyard/service-contracts";

import { useCurrentUser } from "../../../api/hooks";
import {
	useMeetingsIntegrationsAvailableProviders,
	useMeetingsIntegrationsSettings,
	useUserCalendarIntegrationStatus,
	useUserVideoConferencingStatus,
	useVideoConferencingFeatureEnabled,
} from "../../../api/hooks/defs/meetings-integrations";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

// Lives in the api layer so the provider-display-name hook can self-gate on it without a
// circular import; re-exported here for the meetings UI's existing callers.
export { useVideoConferencingFeatureEnabled };

/**
 * Custom hook to manage main navigation active state for meeting workspace details table view.
 * Dynamically sets the correct navigation item based on admin status and participation.
 */
export const useWorkspaceNavigationState = (params: { isAdmin: boolean; isParticipant: boolean }): void => {
	const { isAdmin, isParticipant } = params;

	useEffect(() => {
		const updateMainNavigation = (): void => {
			const allNavItems = document.querySelectorAll("#header .links .top-item");
			allNavItems.forEach((item) => item.classList.remove("active"));

			if (isAdmin && !isParticipant) {
				const companyNavItem = document.querySelector('#header .links a[title="Company Info"]');
				companyNavItem?.classList.add("active");
			} else {
				const meetingWorkspacesNavItem = document.querySelector(
					'#header .links a[href="/meeting-workspaces/"]'
				);
				meetingWorkspacesNavItem?.classList.add("active");
			}
		};

		updateMainNavigation();
	}, [isAdmin, isParticipant]);
};

const SUPPORTED_CALENDAR_PROVIDERS = ["google", "outlook"] as const;

type CalendarIntegrationStatus = {
	isCalendarIntegrationEnabled: boolean;
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined;
	/** True while the flag/settings/status chain is still resolving — eligibility is unknown, not false. */
	isCalendarStatusLoading: boolean;
};

/**
 * Reads the eligibility gates for showing video-conferencing affordances:
 *   1) The video conferencing feature is flag-enabled for the company
 *      (`useVideoConferencingFeatureEnabled` — the single source for that gate),
 *   2) The user has connected at least one available provider,
 *   3) The company admin has enabled video conferencing.
 *
 * Mirrors the backend preHandler gate.
 *
 * Returns `eligible: false` while any underlying query is still loading so the UI
 * never flashes affordances before the gates are resolved.
 */
export const useVideoConferencingEligibility = (): {
	eligible: boolean;
	/** Whether the company admin has enabled video conferencing. Gates the Add/Remove VC affordances. */
	adminEnabled: boolean;
	isLoading: boolean;
	/**
	 * Every provider the user currently has connected — a user can connect many; a meeting uses
	 * exactly one. The add-VC affordances render a checkbox for a single entry and a select when
	 * there are several (labels resolve statically from the provider config). Irrelevant once a
	 * meeting already has VC — removal reads the provider off the stored meeting.
	 */
	connectedProviders: { provider: VideoConferencingProviderEnum }[];
} => {
	const ffEnabled = useVideoConferencingFeatureEnabled();
	const { data: settings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({
		enabled: ffEnabled,
	});
	const adminEnabled = !!settings?.video_conferencing?.enabled;
	// The status/providers endpoints are company-setting-gated (they 403 when VC is off), so only fire
	// them once the company has VC enabled — mirrors useCalendarIntegrationStatus.
	const { data: userStatus, isLoading: isUserStatusLoading } = useUserVideoConferencingStatus({
		enabled: ffEnabled && adminEnabled,
	});
	const { data: providersData, isLoading: isProvidersLoading } = useMeetingsIntegrationsAvailableProviders({
		enabled: ffEnabled && adminEnabled,
	});

	const isLoading = isUserStatusLoading || isProvidersLoading || isSettingsLoading;
	// Intersect the user's connections with the server-driven provider list.
	const connectedProviderNames = new Set((userStatus?.connections ?? []).map((connection) => connection.provider));
	const connectedProviders = (providersData?.providers ?? [])
		.filter(({ provider }) => connectedProviderNames.has(provider))
		.map(({ provider }) => ({ provider }));
	const eligible = ffEnabled && connectedProviders.length > 0 && adminEnabled;

	return { eligible, adminEnabled, isLoading, connectedProviders };
};

/**
 * Whether the meetings integrations FF is on for the company. Gates the calendar icon in the
 * meetings table (independent of whether a calendar is connected).
 */
export const useCalendarFeatureEnabled = (): boolean => {
	const { value: meetingsIntegrationsFFOn } = useFeatureFlagQuery("ff-is-meetings-integrations-enabled", false);
	return !!meetingsIntegrationsFFOn;
};

/** The workspace recurring series cap, from its config flag (matching the backend's clamp). */
export const useRecurringSeriesMaxCount = (): number => {
	const { value: configuredMax } = useFeatureFlagQuery(
		"ff-config-recurring-meetings-max-series",
		DEFAULT_RECURRING_SERIES_LIMIT
	);
	// A configured 0 is a real value; only a non-numeric flag falls back, matching the backend's clamp.
	const parsedMax = Number(configuredMax);
	const effectiveMax = Number.isFinite(parsedMax) ? parsedMax : DEFAULT_RECURRING_SERIES_LIMIT;
	return Math.min(effectiveMax, ABSOLUTE_MAX_RECURRING_SERIES_LIMIT);
};

/**
 * Custom hook to check if calendar integration is enabled for the current user.
 * Consolidates the calendar integration check logic for reuse across components.
 */
export const useCalendarIntegrationStatus = (): CalendarIntegrationStatus => {
	const { value: meetingsIntegrationsFFOn, query: ffQuery } = useFeatureFlagQuery(
		"ff-is-meetings-integrations-enabled",
		false
	);
	const { data: meetingsIntegrationsSettings, isLoading: isSettingsLoading } = useMeetingsIntegrationsSettings({
		enabled: meetingsIntegrationsFFOn,
	});
	const calendarSettingEnabled = meetingsIntegrationsSettings?.calendar?.enabled === true;
	const { data: userCalendarStatus, isLoading: isStatusLoading } = useUserCalendarIntegrationStatus({
		enabled: meetingsIntegrationsFFOn && calendarSettingEnabled,
	});

	// Disabled queries report isLoading false, so each link only counts while its gate is open.
	const isCalendarStatusLoading = Boolean(ffQuery.isLoading || isSettingsLoading || isStatusLoading);

	const isCalendarIntegrationEnabled = Boolean(
		meetingsIntegrationsFFOn &&
		calendarSettingEnabled &&
		userCalendarStatus?.isConnected &&
		SUPPORTED_CALENDAR_PROVIDERS.includes(
			userCalendarStatus?.provider as (typeof SUPPORTED_CALENDAR_PROVIDERS)[number]
		) &&
		userCalendarStatus?.selectedCalendarId
	);

	return { isCalendarIntegrationEnabled, userCalendarStatus, isCalendarStatusLoading };
};

type MeetingCalendarIntegrationState = {
	isCalendarIntegrationEnabled: boolean;
	hasCalendarEvent: boolean;
	isCalendarMismatch: boolean;
	isNonOwnerSeriesEvent: boolean;
};

export type SeriesEventOwnership = "owner" | "non-owner" | "not-series-linked";

/**
 * Whether the meeting's calendar event is a native series occurrence, and if so
 * whether the current user owns it. The owner is persisted on the occurrence link;
 * reading the current series owner would misclassify retained history after reattachment.
 */
export const useSeriesEventOwnership = (meeting: MeetingModelJson | null): SeriesEventOwnership => {
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload.user_id || "";
	const isProvenSeriesOccurrence =
		meeting?.calendarEvent?.recurringSeriesId !== undefined &&
		meeting.calendarEvent.recurringSeriesId === meeting.recurringSeriesId;
	if (!isProvenSeriesOccurrence) {
		return "not-series-linked";
	}
	if (!meeting.calendarEvent?.integrationOwnerId) {
		// Links without persisted owner provenance fail closed.
		return "non-owner";
	}
	return meeting.calendarEvent.integrationOwnerId === currentUserId ? "owner" : "non-owner";
};

/** Whether the meeting's calendar event is a series occurrence on someone else's calendar. */
export const useIsNonOwnerSeriesEvent = (meeting: MeetingModelJson | null): boolean =>
	useSeriesEventOwnership(meeting) === "non-owner";

/**
 * Whether a series meeting's conference is hosted on someone else's account. Removal and
 * upstream edits run with the host's tokens, so non-owners don't get those affordances —
 * the same posture as the calendar options.
 */
export const useIsNonOwnerSeriesVideoConference = (meeting: MeetingModelJson | null): boolean => {
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload.user_id || "";
	return Boolean(
		meeting?.recurringSeriesId &&
		meeting.videoConference?.ownerUserId &&
		meeting.videoConference.ownerUserId !== currentUserId
	);
};

/**
 * Custom hook to determine calendar integration state for a meeting.
 * Extracts complex calendar logic from components for better testability and reuse.
 */
export const useMeetingCalendarIntegration = (meeting: MeetingModelJson): MeetingCalendarIntegrationState => {
	const { isCalendarIntegrationEnabled, userCalendarStatus } = useCalendarIntegrationStatus();
	const seriesEventOwnership = useSeriesEventOwnership(meeting);

	const hasCalendarEvent = Boolean(meeting.calendarEvent?.eventId);

	// An owned series occurrence lives on the calendar its master was created on; the owner's
	// tokens still reach it if they select another calendar later, so only the provider must
	// match — mirroring the backend's owner rule.
	const isCalendarMismatch = Boolean(
		meeting.calendarEvent?.eventId &&
		meeting.calendarEvent?.provider &&
		userCalendarStatus &&
		!(
			userCalendarStatus.provider === meeting.calendarEvent.provider &&
			(seriesEventOwnership === "owner" ||
				userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId)
		)
	);

	return {
		isCalendarIntegrationEnabled,
		hasCalendarEvent,
		isCalendarMismatch,
		isNonOwnerSeriesEvent: seriesEventOwnership === "non-owner",
	};
};
